<template>
  <div class="about-page">
    <!-- Hero Section -->
    <section class="hero">
      <img src="https://www-img.sanfu.com/production/aboutus.png" alt="三福品牌" class="hero-image" />
      <div class="hero-content">
        <div class="icon">
          <div class="sfwww a2 front"></div>
          <div class="sfwww a2 back"></div>
        </div>
        <p class="hero-description">
          {{ $t('about.hero.line1') }}
          <br />
          {{ $t('about.hero.line2') }}
          <br />
          {{ $t('about.hero.line3') }}
          <br />
        </p>
      </div>
      <!-- Scroll Indicator -->
      <div class="hero-scroll-indicator">
        <div class="scroll-arrow"></div>
      </div>
    </section>

    <!-- Brand Story -->
    <section class="brand-section">
      <div class="section-logo-text">BRAND STORY</div>
      <h2 v-if="$t('common.locale') !== 'en'" class="section-title">{{ $t('about.brand.story.title') }}</h2>
      <div class="section-content">
        {{ $t('about.brand.story.description') }}
      </div>
    </section>
    <!-- 副标题 -->
    <section class="vision-mission-wrap">
      <div class="vision-mission-item">
        <span class="vm-logo">VISION</span>
        <span v-if="$t('common.locale') !== 'en'" class="vm-title">{{ $t('about.brand.vision.title') }}</span>
        <p class="vm-description">{{ $t('about.brand.vision.description') }}</p>
      </div>
      <div class="vision-mission-item">
        <span class="vm-logo">MISSION</span>
        <span v-if="$t('common.locale') !== 'en'" class="vm-title">{{ $t('about.brand.mission.title') }}</span>
        <p class="vm-description">{{ $t('about.brand.mission.description') }}</p>
      </div>
    </section>
    <!-- Brand History -->
    <section class="brand-section brand-history">
      <div class="section-logo-text">BRAND HISTORY</div>
      <h2 v-if="$t('common.locale') !== 'en'" class="section-title">{{ $t('about.brand.history.title') }}</h2>

      <div class="timeline-wrap">
        <div class="timeline"></div>
        <div v-for="(item, index) in brandHistory" :key="index" class="timeline-item" :class="{ 'timeline-item-right': index % 2 === 1 }">
          <div class="timeline-year">{{ item.year }}</div>
          <div class="timeline-marker">
            <div class="timeline-dot"></div>
            <div class="timeline-line" v-if="index < brandHistory.length - 1"></div>
            <img v-if="item.image" class="timeline-image" :src="item.image" :alt="item.title[$t('common.locale')]" />
          </div>
          <div class="timeline-content">
            <h3 class="timeline-title">{{ item.title[$t('common.locale')] }}</h3>
            <p class="timeline-description">{{ item.description[$t('common.locale')] }}</p>
            <img v-if="item.image" class="timeline-image2" :src="item.image" :alt="item.title[$t('common.locale')]" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted } from 'vue'

  // i18n工具函数
  const { t } = useI18n()

  useHead({
    title: t('navigation.about') + ' - SANFU',
    meta: [{ name: 'description', content: t('about.meta.description') }]
  })

  // 组件挂载时添加事件监听器
  onMounted(() => {
    // 添加滚动动画
    const observerOptions = {
      threshold: 0.001, // 最低阈值
      rootMargin: '10px' // 提前50px触发
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in')
        }
      })
    }, observerOptions)

    // 观察需要动画的元素
    const animateElements = document.querySelectorAll('.section-logo-text, .section-title, .section-content, .vision-mission-item, .timeline-item')
    animateElements.forEach(el => observer.observe(el))
  })

  // 组件卸载时移除事件监听器
  onUnmounted(() => {})

  // 品牌历程数据
  const brandHistory = ref([
    {
      year: '1992',
      title: {
        zh: '我们在福建诞生了',
        en: 'We were born in Fujian',
        id: 'Kami lahir di Fujian'
      },
      description: {
        zh: '三福百货前身-福州融新百货开业，主营男女装，兼营饰品及内衣，为当地年轻人开启时尚的第一步',
        en: "Sanfu Department Store predecessor - Fuzhou Rongxin Department Store opened, mainly selling men's and women's clothing, as well as accessories and underwear, taking the first step in fashion for local young people",
        id: 'Pendahulu Sanfu Department Store - Fuzhou Rongxin Department Store dibuka, terutama menjual pakaian pria dan wanita, serta aksesori dan pakaian dalam, mengambil langkah pertama dalam fashion bagi pemuda lokal'
      },
      image: 'https://www-img.sanfu.com/production/history1.png'
    },
    {
      year: '1998',
      title: {
        zh: '我们走出福建，期待与全国的年轻人相遇',
        en: 'We stepped out of Fujian, looking forward to meeting young people across the country',
        id: 'Kami melangkah keluar dari Fujian, menantikan untuk bertemu pemuda di seluruh negeri'
      },
      description: {
        zh: '安徽合肥长江路店开业，三福走出福建开始了全国连锁的步伐，期待与更多年轻人分享潮流生活',
        en: 'Anhui Hefei Changjiang Road store opened, Sanfu stepped out of Fujian and began its national chain journey, looking forward to sharing trendy life with more young people',
        id: 'Toko Anhui Hefei Changjiang Road dibuka, Sanfu melangkah keluar dari Fujian dan memulai perjalanan rantai nasional, menantikan untuk berbagi kehidupan trendi dengan lebih banyak pemuda'
      },
      image: 'https://www-img.sanfu.com/production/history2.png'
    },
    {
      year: '2007',
      title: {
        zh: '我们在80多个城市生根发芽，与你们共同成长',
        en: 'We took root and grew in more than 80 cities, growing together with you',
        id: 'Kami berakar dan tumbuh di lebih dari 80 kota, tumbuh bersama dengan Anda'
      },
      description: {
        zh: '在80多个城市设立110多家连锁店，员工逾5000人，三福不仅在地理上扩展，更在服务和产品上成为年轻一代的潮流趋势',
        en: 'With more than 110 chain stores in more than 80 cities and over 5,000 employees, Sanfu not only expanded geographically, but also became a trend for the younger generation in terms of service and products',
        id: 'Dengan lebih dari 110 toko rantai di lebih dari 80 kota dan lebih dari 5.000 karyawan, Sanfu tidak hanya berkembang secara geografis, tetapi juga menjadi tren bagi generasi muda dalam hal layanan dan produk'
      },
      image: 'https://www-img.sanfu.com/production/history3.png'
    },
    {
      year: '2011',
      title: {
        zh: '从这一年开始，变成你们最熟悉的样子',
        en: 'From this year on, we became the familiar look to you',
        id: 'Dari tahun ini, kami menjadi tampilan yang familiar bagi Anda'
      },
      description: {
        zh: '启用由日本著名平面空间派大师林广行主设计的"九宫格"新logo，以全新的形象，继续给年轻人提供时尚生活',
        en: 'Adopted the new "nine-grid" logo designed by renowned Japanese graphic space master Lin Guangxing, with a brand new image, continuing to provide fashionable life for young people',
        id: 'Mengadopsi logo "nine-grid" baru yang dirancang oleh master grafis ruang Jepang terkenal Lin Guangxing, dengan citra baru, terus menyediakan kehidupan fashionable bagi pemuda'
      },
      image: 'https://www-img.sanfu.com/production/history4.png'
    },
    {
      year: '2015',
      title: {
        zh: '“去三福逛逛”成为年轻人的时尚代名词',
        en: '"Go to Sanfu for a visit" became the fashion synonym for young people',
        id: '"Pergi ke Sanfu untuk berkunjung" menjadi sinonim fashion bagi pemuda'
      },
      description: {
        zh: '成立河北大区；开拓东北市场；开始与以万达为首的购物中心达成品牌合作，将潮流生活带给超过200家购物中心的顾客',
        en: 'Established Hebei region; expanded Northeast market; began brand cooperation with shopping centers led by Wanda, bringing trendy life to customers of more than 200 shopping centers',
        id: 'Mendirikan wilayah Hebei; mengembangkan pasar Northeast; memulai kerjasama merek dengan pusat perbelanjaan yang dipimpin Wanda, membawa kehidupan trendi kepada pelanggan lebih dari 200 pusat perbelanjaan'
      },
      image: 'https://www-img.sanfu.com/production/history5.png'
    },
    {
      year: '2021',
      title: {
        zh: '我们再次迈出了新的一步，希望和你们一起去发现生活的新可能',
        en: 'We took another new step, hoping to discover new possibilities in life with you',
        id: 'Kami mengambil langkah baru lagi, berharap untuk menemukan kemungkinan baru dalam kehidupan bersama Anda'
      },
      description: {
        zh: '三福品牌升级，开出第一家生活方式集合店，为年轻一代提供更多元化的潮流选择',
        en: 'Sanfu brand upgrade, opened the first lifestyle collection store, providing younger generations with more diversified trendy choices',
        id: 'Upgrade merek Sanfu, membuka toko koleksi gaya hidup pertama, menyediakan pilihan trendi yang lebih beragam bagi generasi muda'
      },
      image: 'https://www-img.sanfu.com/production/history6.png'
    },
    {
      year: '2023',
      title: {
        zh: '从一棵种子到枝繁叶茂，我们和年轻人一起成长',
        en: 'From a seed to lush foliage, we grow together with young people',
        id: 'Dari sebutir benih hingga rimbun, kami tumbuh bersama pemuda'
      },
      description: {
        zh: '三福拥有门店逾1000家，遍布全国大中小城市，我们不仅见证了一代又一代年轻人的成长，也在不断自我革新中，与他们一起探索新的未来',
        en: 'Sanfu has over 1,000 stores, spread across large, medium and small cities nationwide. We not only witness the growth of generation after generation of young people, but also continuously innovate ourselves, exploring new futures with them',
        id: 'Sanfu memiliki lebih dari 1.000 toko, tersebar di kota besar, sedang dan kecil di seluruh negeri. Kami tidak hanya menyaksikan pertumbuhan generasi demi generasi pemuda, tetapi juga terus menginovasi diri, menjelajahi masa depan baru bersama mereka'
      },
      image: 'https://www-img.sanfu.com/production/history7.png'
    }
  ])
</script>

<style scoped lang="less">
  .about-page {
    margin-top: -70px;
  }

  /* 顶部图片区域 */
  .hero {
    width: 100%;
    position: relative;
    min-height: 70vh;
    overflow: hidden;
    display: flex;
  }

  .hero-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: left top; /* 关键：靠左上对齐 */
  }

  .hero-content {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    text-align: center;
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .icon {
      font-size: 6.1em;
      position: relative;
      color: #fff;
      .front {
        font-size: inherit;
        mix-blend-mode: overlay;
        top: 0;
      }
      .back {
        position: absolute;
        font-size: inherit;
        top: 1px;
        left: 2px;
        opacity: 0.15;
      }
    }
  }

  .hero-description {
    font-size: 1em;
    opacity: 0.85;
    font-weight: 400;
  }

  /* Scroll Indicator */
  .hero-scroll-indicator {
    position: absolute;
    bottom: var(--space-16);
    left: 50%;
    transform: translateX(-50%);
    z-index: 3;
    animation: bounce 2s infinite;
  }

  .scroll-arrow {
    width: 24px;
    height: 24px;
    border: 2px solid var(--white);
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateX(-50%) translateY(0);
    }
    40% {
      transform: translateX(-50%) translateY(-10px);
    }
    60% {
      transform: translateX(-50%) translateY(-5px);
    }
  }

  .brand-section {
    padding: var(--space-24) var(--space-20);
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: var(--space-24) 0;
  }

  .section-logo-text {
    font-size: 70px;
    font-weight: 700;
    letter-spacing: -3px;
    background: linear-gradient(180deg, #c7c7c7 20%, #f3f3f3);
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    line-height: 1;
    margin-bottom: var(--space-4);
    user-select: none;
    opacity: 0;
    transform: translateY(30px);
    transition: all var(--transition-base);
  }

  .section-title {
    user-select: none;
    font-size: 46px;
    font-weight: 600;
    margin-bottom: var(--space-8);
    color: var(--gray-900);
    opacity: 0;
    transform: translateY(30px);
    transition: all var(--transition-base);
  }

  .section-content {
    margin-top: var(--space-4);
    white-space: pre-wrap;
    color: #808080;
    font-size: 18px;
    line-height: 2;
    max-width: 800px;
    word-break: break-word;
    text-align: center;
    opacity: 0;
    transform: translateY(30px);
    transition: all var(--transition-base);
  }

  .vision-mission-wrap {
    display: flex;
    padding: 0 10vw;
    justify-content: space-around;
    background-image: url(https://www-img.sanfu.com/production/about_sub.png);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    height: 400px;
    text-align: center;
  }

  .vision-mission-item {
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
    flex: 1;
    flex-shrink: 0;
    user-select: none;
    opacity: 0;
    transform: translateY(30px);
    transition: all var(--transition-base);
  }

  .vm-logo {
    // height: 150px;
    display: flex;
    align-items: flex-end;
    font-size: 70px;
    font-weight: 700;
    background: linear-gradient(180deg, #f1f1f1 20%, #a0a0a020);
    opacity: 0.8;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    margin-bottom: var(--space-1);
  }
  .vm-title {
    font-size: 46px;
    font-weight: 600;
  }

  .vm-description {
    margin-top: var(--space-2);
    font-size: 18px;
    color: #dcdcda;
  }

  /* Brand History Timeline */
  .brand-history {
    position: relative;
    overflow: hidden;
    margin-top: calc(10vh + var(--space-24));
    padding: 0 var(--space-16);
  }

  .brand-history::before {
    content: '';
    position: absolute;
    background-image: url(https://www-img.sanfu.com/production/earth.png);
    width: 80%;
    height: 100%;
    background-size: contain;
    background-repeat: no-repeat;
    z-index: -1;
    top: 200px;
  }

  .timeline-wrap {
    position: relative;
    width: calc(100% - 40px);
    max-width: 900px;
    margin: 0 auto;
    overflow: visible;
    padding-top: 100px;
  }
  .timeline-wrap::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(180deg, #ffffff, #565656 10%, #565656 90%, #ffffff);
    top: 0;
  }
  .timeline-item {
    position: relative;
    padding-right: 20px;
    margin-bottom: var(--space-8);
    display: flex;
    flex-direction: column;
    width: 50%;
    text-align: right;
    align-items: flex-end;
    opacity: 0;
    transform: translateY(30px);
    transition: all var(--transition-base);
  }

  .timeline-item-right {
    text-align: left;
    margin-left: auto;
    padding-right: 0;
    padding-left: 20px;
    align-items: flex-start;
  }

  .timeline-year {
    font-size: 46px;
    font-weight: 700;
    color: #000;
    line-height: 1;
    margin-bottom: var(--space-4);
  }

  .timeline-marker {
    display: flex;
    justify-content: flex-end;
    position: relative;
    right: -20px;
    margin-bottom: var(--space-4);
    width: 70%;
    background-image: radial-gradient(#c0c4cc 1px, transparent 1px);
    background-size: 4px 6px;
    background-repeat: repeat-x;
    background-position: 0 -1px;
  }

  .timeline-item-right .timeline-marker {
    left: -20px;
    justify-content: flex-start;
  }

  .timeline-dot {
    position: relative;
    right: -2px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: #fff;
    box-shadow: 0 0 0 2px var(--gray-600);
    z-index: 2;
  }

  .timeline-item-right .timeline-dot {
    left: -2px;
    right: 0;
  }

  .timeline-line {
    position: absolute;
    flex: 1;
    width: 2px;
    background-color: var(--gray-300);
    margin-top: var(--space-2);
  }

  .timeline-image {
    position: absolute;
    width: 90%;
    left: 0;
    border-radius: 4px;
    transform: translateX(-100%) translateY(-50%);
  }

  .timeline-item-right .timeline-image {
    left: unset;
    right: 0;
    transform: translateX(100%) translateY(-50%);
    border: 1px solid var(--gray-200);
  }

  .timeline-content {
    flex: 1;
    width: 60%;
  }

  .timeline-title {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: var(--space-3);
    color: var(--gray-900);
  }

  .timeline-description {
    font-size: 16px;
    color: #808080;
    line-height: 1.5;
    margin-bottom: unset;
  }

  .timeline-image2 {
    display: none;
  }

  @media (min-width: 769px) {
    .hero-image {
      min-height: 70vh;
      max-height: 100vh;
    }
  }
  @media (max-width: 768px) {
    .hero {
      height: 70vh;
    }
    .hero-title {
      font-size: var(--text-3xl);
    }
    .hero-content .icon {
      font-size: 4.5rem;
    }

    .section-content {
      line-height: 1.8;
    }

    .vision-mission-wrap {
      padding: 0 16px;
      gap: 20px;
      height: 200px;
    }
    .vm-logo {
      opacity: 0.9;
      font-size: 50px;
    }
    .vm-title {
      font-size: 28px;
    }
    .vm-description {
      font-size: 18px;
    }
    .brand-history {
      margin-top: calc(5vh + var(--space-24));
    }
    .timeline-wrap::before {
      left: 0;
      transform: unset;
    }
    .timeline-item {
      padding-left: 30px;
      padding-right: 0;
      text-align: left;
      width: 100%;
      align-items: flex-start;
    }
    .timeline-item-right {
      margin-left: unset;
    }
    .timeline-item-right .timeline-marker,
    .timeline-marker {
      left: -28px;
      right: unset;
      justify-content: flex-start;
      width: calc(65% + 36px);
    }
    .timeline-dot {
      left: -2px;
      right: unset;
    }
    .timeline-image {
      width: 50%;
      left: unset;
      right: 0;
      transform: translateX(100%) translateY(-50%);
    }

    .timeline-item-right .timeline-content {
      text-align: left;
    }

    .timeline-content {
      width: 60%;
      margin-bottom: var(--space-8);
      .timeline-title{
        font-size: 20px;
      }
      .timeline-description{
        font-size: 16px;
      }
    }

    .hero-scroll-indicator {
      bottom: var(--space-12);
    }
  }
  @media (max-width: 480px) {
    .hero {
      height: 50vh;
    }

    .hero-description {
      font-size: 0.85em;
    }
    .hero-content .icon {
      font-size: 3.5rem;
      text-shadow: 2px 2px 2px rgba(255, 255, 255, 0.3), -1px -1px 1px rgba(0, 0, 0, 0.1);
      opacity: 0.65;
    }
    .hero-scroll-indicator {
      bottom: var(--space-8);
    }

    .brand-section{
      padding: 0 var(--space-12);
    }

    .section-logo-text {
      text-align: left;
      width: 100%;
      font-size: 46px;
    }
    .section-title {
      text-align: left;
      width: 100%;
      font-size: 32px;
      margin-bottom: var(--space-4);
    }
    .section-content {
      font-size: 16px;
      line-height: 1.6;
      text-align: left;
    }

    .vision-mission-wrap {
      padding: 0;
      height: 160px;
      gap: 16px;
      padding: 0 16px;
    }
    .vm-logo {
      font-size: 36px;
      margin-bottom: var(--space-2);
      opacity: 0.9;
    }
    .vm-title {
      font-size: 20px;
    }
    .vm-description {
      font-size: 12px;
      letter-spacing: -0.1px;
      word-break: break-all;
    }

    .brand-history {
      margin-top:var(--space-24);
    }

    .timeline-wrap {
      width: 100%;
      padding-top: 50px;
    }

    .timeline-wrap::before {
      left: -10px;
    }

    .timeline-item {
      padding-left: 10px;
    }

    .timeline-item-right .timeline-marker,
    .timeline-marker {
      background: unset;
      margin: unset;
      top: -30px;
      left: -18px;
    }

    .timeline-marker .timeline-image {
      display: none;
    }
    .timeline-year{
      font-size: 32px;
    }
    .timeline-content {
      width: 100%;
      .timeline-image2 {
        margin-top: var(--space-3);
        display: block;
        border-radius: 4px;
        border: 1px solid var(--gray-200);
        width: 100%;
      }
    }
  }
  .fade-in {
    opacity: 1;
    transform: translateY(0);
  }
</style>
